import React, { FC, useRef, useState } from 'react';
import { TrashCanOutlined } from '@duxton-web/icons';
import {
  Button,
  Form,
  Input,
  notification,
  Select,
  Typography,
} from '@duxton-web/ui';

import Service, { UpdateStateResponse } from '../../lib/App';

import './RejectPrompt.css';

import { FormLayout } from '@duxton-web/ui/es/form/Form';

import { HTTP_STATUS } from '@/constants';

const notificationApi = notification;

interface RejectType {
  value: string;
  text: string;
  reasonRequired: boolean;
}

export const rejectTypes: RejectType[] = [
  {
    value: 'notWorking',
    text: 'I am rejecting as moderator because the Experiment configuration is incorrect.',
    reasonRequired: true,
  },
  {
    value: 'facetNotInstrumented',
    text: 'I am rejecting as moderator because one or more facets used in the experiment were not instrumented.',
    reasonRequired: true,
  },
  {
    value: 'interference',
    text: 'I am rejecting as overlapping experiment owner because this experiment has too much inferences with my overlapping experiment.',
    reasonRequired: false,
  },
  {
    value: 'other',
    text: 'Other reason',
    reasonRequired: true,
  },
];

interface RejectPromptProps {
  experiment: string;
  isAdaptiveExp?: boolean;
  layout?: FormLayout;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const RejectPrompt: FC<RejectPromptProps> = ({
  experiment,
  isAdaptiveExp,
  layout,
  onCancel,
  onSuccess,
}) => {
  const [rejectType, setRejectType] = useState<{
    value: string;
    error: boolean;
  }>({
    value: '',
    error: false,
  });
  const [reasonError, setReasonError] = useState<string>('');

  const reason = useRef<string>('');
  const rejectTypeObj = rejectTypes.find((rt) => rt.value == rejectType.value);

  function validate(): boolean {
    if (!rejectType.value) {
      setRejectType({
        ...rejectType,
        error: true,
      });

      return false;
    }

    const rejectTypeObj = rejectTypes.find(
      (rt) => rt.value == rejectType.value,
    );
    if (rejectTypeObj!.reasonRequired) {
      if (!reason.current) {
        setReasonError('This field is required');

        return false;
      }
      const cleanReason = reason.current.trim();
      if (cleanReason.length <= 10) {
        setReasonError(
          'An ideal reason is usually more than 10 characters long. Please provide a valid reason.',
        );

        return false;
      }
    }
    return true;
  }
  async function reject(): Promise<void> {
    if (!validate()) {
      return;
    }

    try {
      await Service.planner
        .changeExperimentState(
          experiment,
          'reject',
          `${rejectType.value}:${reason.current}`,
          isAdaptiveExp,
        )
        .then((res: UpdateStateResponse) => {
          if ((!isAdaptiveExp && res) || isAdaptiveExp) {
            notificationApi.success({
              message: 'Rejecting an Experiment',
              description: 'Experiment was successfully rejected.',
            });
            onSuccess();
          }
        });
    } catch (error) {
      let description =
        'Experiment request was not rejected, due to: ' + error.toString();
      if (error.response && error.response.status === HTTP_STATUS.FORBIDDEN) {
        description =
          'Experiment request was not rejected, due to: not enough permissions';
      }
      notificationApi.error({
        description,
        message: 'Rejecting an Experiment',
      });
    }
  }

  function handleRejectType(value: string): void {
    setRejectType({
      value,
      error: false,
    });
  }

  function handleReason(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    reason.current = event.target.value;

    setReasonError('');
  }

  return (
    <div className="reject-prompt__container">
      <div className="reject-prompt__header">
        <Typography.Text strong type="danger">
          {experiment}
        </Typography.Text>
      </div>
      <Form layout={layout}>
        <Form.Item
          label="Select reason why you are rejecting this experiment?"
          required
          validateStatus={rejectType.error ? 'error' : ''}
          help={rejectType.error ? 'This field is required' : ''}
        >
          <Select onChange={handleRejectType}>
            {rejectTypes.map((rt) => (
              <Select.Option key={rt.value} value={rt.value}>
                {rt.text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Additional reasons. Please provide a valid reason to help us accelerate Experimentation at Grab."
          required={rejectTypeObj && rejectTypeObj.reasonRequired}
          validateStatus={reasonError ? 'error' : ''}
          help={reasonError ? reasonError : ''}
        >
          <Input.TextArea rows={8} onChange={handleReason} />
        </Form.Item>
        <Form.Item>
          {onCancel && (
            <Button size="small" onClick={onCancel}>
              Back
            </Button>
          )}
          <Button
            size="small"
            danger
            icon={<TrashCanOutlined />}
            className="reject-prompt__reject-button"
            onClick={reject}
          >
            Reject
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default RejectPrompt;
